// Pure functions for recommendation calculation
// Based on backend specification routing logic

export function calculateRecommendation(q1Tag, q2Tag, q3Tag, q4Tag) {
  const corporateFlag = q1Tag === 'biz' || q2Tag === 'hosting';
  
  // Priority 1: Premium override (Q4 = "elevated")
  if (q4Tag === 'elevated') {
    return {
      plan: 'premium',
      premiumFlag: true,
      corporateFlag
    };
  }
  
  // Priority 2-4: Based on Q3 (season experience)
  let plan;
  if (q3Tag === 'every') {
    plan = 'full';
  } else if (q3Tag === 'some') {
    plan = 'half';
  } else if (q3Tag === 'unsure') {
    plan = 'undecided';
  } else {
    // Fallback
    plan = 'undecided';
  }
  
  return {
    plan,
    premiumFlag: false,
    corporateFlag
  };
}

export function getQuestionData() {
  // Hardcoded questions based on backend specification
  // In production, these could be fetched from API
  return {
    q1: {
      eyebrow: 'Question 1 of 4',
      title: 'How would you describe yourself <em>as a fan?</em>',
      body: 'Pick the one that feels most like you.',
      options: [
        { marker: 'A', text: "I've been following the WNBA for years", tag: 'loyalist' },
        { marker: 'B', text: "I love basketball — I'll watch great hoops anywhere", tag: 'loves_bball' },
        { marker: 'C', text: "I'm a Cleveland sports fan ready to add a new team", tag: 'crossover' },
        { marker: 'D', text: "I follow athletes and culture as much as the game", tag: 'culture' },
        { marker: 'E', text: "I'm newer to basketball but excited about what's happening here", tag: 'moment' },
        { marker: 'F', text: "I'm exploring this for my business or group", tag: 'biz', className: 'biz' }
      ]
    },
    q2: {
      eyebrow: 'Question 2 of 4',
      title: 'What does your <em>ideal game day</em> look like?',
      body: 'Pick the one that feels most like you.',
      options: [
        { marker: 'A', text: 'Locked in from tip-off, here for the game', tag: 'diehard' },
        { marker: 'B', text: 'Great seats, great atmosphere, great night out', tag: 'social' },
        { marker: 'C', text: 'Making it a family tradition', tag: 'family' },
        { marker: 'D', text: 'Hosting clients or a group, showing off the city', tag: 'hosting', className: 'biz' },
        { marker: 'E', text: "I just want to be part of something new in Cleveland", tag: 'newcomer' }
      ]
    },
    q3: {
      eyebrow: 'Question 3 of 4',
      title: 'How do you see yourself <em>experiencing the season?</em>',
      body: 'This helps us match you with the right plan.',
      options: [
        { marker: 'A', text: 'I want to be there for every home game', tag: 'every' },
        { marker: 'B', text: "I'd love to catch games throughout the season", tag: 'some' },
        { marker: 'C', text: "I'm still figuring out what works for me", tag: 'unsure' }
      ]
    },
    q4: {
      eyebrow: 'Question 4 of 4',
      title: 'What would make this membership feel <em>worth it?</em>',
      body: 'Select what matters most to you.',
      options: [
        { marker: 'A', text: 'Knowing I have my seats locked in every game', tag: 'access' },
        { marker: 'B', text: "Access to things regular fans don't get", small: 'The spaces, the moments, the exclusivity', tag: 'experience' },
        { marker: 'C', text: 'Being part of the founding community — I was here first', tag: 'community' },
        { marker: 'D', text: 'Flexibility to fit my schedule', tag: 'value' },
        { marker: 'E', text: 'Perks beyond basketball — events, discounts, experiences', tag: 'perks' },
        { marker: 'F', text: 'The most elevated experience — the best seats, private club spaces, premium hospitality', tag: 'elevated', className: 'premium-answer' }
      ]
    }
  };
}

export function getResultContent(plan, corporateFlag) {
  const content = {
    full: {
      eyebrow: 'Your membership match',
      planLabel: 'Full Season — <em>Season Ticket Membership</em>',
      basedOn: 'Based on your answers, this is your fit.',
      description: 'All home games — the complete Cleveland WNBA experience from opening night through the final buzzer.',
      benefits: [
        'Priority position for Select-a-Seat based on your Initial Payment date',
        'Select-a-Seat gift — an exclusive gift for attending seat selection',
        'Fan Fest gift — an exclusive giveaway for IP holders',
        'Fan Fest VIP access — priority entry and exclusive event access'
      ],
      quotes: [
        { text: '"Cleveland\'s just a basketball town. I\'ve been wanting a WNBA team here for years — this was a no-brainer."', attr: 'Founding Member, Seven Hills' },
        { text: '"It\'s part of Cleveland\'s broader comeback story. The timing couldn\'t be better."', attr: 'Founding Member, Cleveland Heights' }
      ]
    },
    half: {
      eyebrow: 'Your membership match',
      planLabel: 'Half Season — <em>Season Ticket Membership</em>',
      basedOn: 'Based on your answers, this is your fit.',
      description: 'Half of all home games — flexible options designed around your schedule without sacrificing the experience.',
      benefits: [
        'Priority position for Select-a-Seat based on your Initial Payment date',
        'Select-a-Seat gift — an exclusive gift for attending seat selection',
        'Fan Fest gift — an exclusive giveaway for IP holders',
        'Fan Fest VIP access — priority entry and exclusive event access'
      ],
      quotes: [
        { text: '"Now my daughter is going to have the ability to go to WNBA games — people she can actually look up to, right here at home."', attr: 'Founding Member, Parma Heights' },
        { text: '"I had such FOMO watching my brother with his Liberty season tickets. Now I finally have my own hometown team."', attr: 'Founding Member, Cleveland' }
      ]
    },
    undecided: {
      badge: 'Start here.',
      title: "You don't need to have it all figured out.",
      paragraphs: [
        "The fans who place their Initial Payment earliest get the best seats when selection opens in Spring 2027. That's true whether you end up Full Season, Half Season, or anything in between.",
        "Cleveland's first WNBA team is being built right now, and the people who show up early are the ones who'll have been here from the beginning. There's no better seat than that.",
        "Your $28 per-seat Initial Payment holds your spot — and can be applied toward any membership or group product when you're ready."
      ]
    },
    premium: {
      badge: 'Premium Membership',
      title: "You're after <em>the best of it.</em>",
      paragraphs: [
        "Premium Membership is for the fan who wants first access to the most elevated experiences Cleveland WNBA basketball will offer — the best seats, private club spaces, and premium hospitality when the doors open in 2028.",
        "Priority is everything at this tier, and it starts now."
      ]
    }
  };
  
  return content[plan] || content.undecided;
}

export function getFAQData() {
  return [
    {
      question: 'Is this a deposit or a membership?',
      answer: "It's the first step toward a membership. Your $28 Initial Payment secures your priority position; you choose and confirm your specific plan and seats at Select-a-Seat in Spring 2027."
    },
    {
      question: 'What does the $28 actually get me?',
      answer: 'Priority position in line for seat selection — the earlier you place it, the better your seats — plus IP holder benefits ahead of the season.'
    },
    {
      question: 'What if I change my mind about which plan I want?',
      answer: "Your Initial Payment isn't locked to one plan. It can be applied toward any membership or group product, so you're free to choose what fits when seat selection opens."
    },
    {
      question: 'When do I pay for my actual tickets?',
      answer: 'At Select-a-Seat in Spring 2027, when you select seats and confirm your plan. Monthly payment options will be available then.'
    },
    {
      question: "What if I can't make every game?",
      answer: "Half Season memberships are built around a flexible schedule, and all members get ticket exchange flexibility for games they can't attend."
    },
    {
      question: 'Can I choose my seats now?',
      answer: 'Not yet — seat selection opens in Spring 2027. Your Initial Payment timing determines your priority when it does.'
    },
    {
      question: 'What\'s the difference between Full and Half Season?',
      answer: 'Full Season is every home game. Half Season is a flexible half of the schedule. Both are full memberships with the same benefits.'
    }
  ];
}
